import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    StyleSheet,
    View,
    SafeAreaView,
    FlatList,
    ActivityIndicator,
    useWindowDimensions,
    TouchableOpacity,
    Text
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useTableHelper } from "../../../hooks/useTableHelper";
import { useSettingsContext } from '../../../hooks/useSettingsContext';
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useCompany } from "../../../hooks/useCompany";
import { v4 as uuidv4 } from 'uuid';
import { showMessage } from "react-native-flash-message";


const JobTitlesListView = React.memo(
    ({
        item,
        index,
        handleEditPress,
        styles,
        t,
        handleDelete,
        d365
    }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const toggleExpand = () => {
            setIsExpanded(!isExpanded);
        };
        return (
            <View style={styles.container}>
                <SafeAreaView style={styles.row}>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.jobtitle}
                            setValue={(text) => {
                                handleEditPress(index, item, "jobtitle", text);
                            }}
                            editable={true}
                            maxLen={50}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.item}>
                        <CustomInput
                            value={item.description}
                            setValue={(text) => {
                                handleEditPress(index, item, "description", text);
                            }}
                            editable={true}
                            maxLen={50}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.abbreviation}
                            setValue={(text) => {
                                handleEditPress(index, item, "abbreviation", text);
                            }}
                            editable={true}
                            maxLen={50}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.jobtitleDME}
                            setValue={(text) => {
                                handleEditPress(index, item, "jobtitleDME", text);
                            }}
                            editable={true}
                            maxLen={50}
                            type="MAININPUT"
                        />
                    </View>

                    <SafeAreaView>
                        <CustomButton
                            iconName="trash-outline"
                            onPress={() => handleDelete(item, index, "rowGuid")}
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={30}
                        />
                        {d365 && (
                            <Ionicons
                                name={isExpanded ? "chevron-down-outline" : "chevron-forward-outline"}
                                style={styles.icons}
                                onPress={toggleExpand}
                            />
                        )}
                    </SafeAreaView>
                    {isExpanded && (
                        <SafeAreaView style={styles.row}>
                            <View style={styles.item}>
                                <CustomLabel value={t('common:alternativeDescription')} type='SUBTITLE' />
                                <CustomInput
                                    value={item.d365FDCode}
                                    setValue={(e) => handleEditPress(index, item, 'alternativeDescription', e)}
                                    maxLen={50}
                                    type="MAININPUT"
                                />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={t('common:alternativeAbbreviation')} type='SUBTITLE' />
                                <CustomInput
                                    value={item.d365FDCode}
                                    setValue={(e) => handleEditPress(index, item, 'alternativeAbbreviation', e)}
                                    maxLen={50}
                                    type="MAININPUT"
                                />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={t('common:d365Code')} type='SUBTITLE' />
                                <CustomInput
                                    value={item.d365Code}
                                    setValue={(e) => handleEditPress(index, item, 'd365Code', e)}
                                    maxLen={50}
                                    type="MAININPUT"
                                />
                            </View>
                        </SafeAreaView>
                    )}
                </SafeAreaView>
            </View>
        );
    }
);

const HRAdminJobTitleScreen = (props) => {
    const [jobTitle, setJobTitle] = useState([]);
    const [originalJobTitle, setOriginalJobTitle] = useState([]);
    const [changes, setChanges] = useState({});
    const [deletedItems, setDeletedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef(null);
    const { t } = useTranslation();
    const { rowAdd, rowEdit, rowDelete } = useTableHelper();
    const { getCompanyJobTitles, saveJobTitles, deleteJobTitles } = useCompany();
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);
    const common = createCommonStyles(colors);
    const { d365 } = useSettingsContext();
    const [duplicates, setDuplicates] = useState([])
    const { userId } = useAuthContext();
    const [sortOrder, setSortOrder] = useState("asc");

    const loadJobTitles = async () => {
        if (!props.selectedCompany) {
            setJobTitle([]);
            setOriginalJobTitle([]);
            return;
        }
        setIsLoading(true);
        try {
            const fetchedJobTitles = await getCompanyJobTitles(props.selectedCompany);
            const sortedJobTitles = sortJobTitles(fetchedJobTitles, "asc");
            setJobTitle(sortedJobTitles);
            setOriginalJobTitle(fetchedJobTitles);
        } catch (error) {
            showMessage({
                message: t("common:Errorloadingjobtitles:"),
                type: "info",
                duration: 3000,
            });
            setJobTitle([]);
            setOriginalJobTitle([]);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        loadJobTitles();
    }, [props.selectedCompany]);

    const removeFromChangesAfterSave = () => {
        setChanges({});
        setDeletedItems([]);
    };

    useEffect(() => {
        const fetchedData = [];
        setOriginalJobTitle(fetchedData);
    }, []);


    const handleEditPress = useCallback(
        (index, item, key, value) => {
            if (item[key] === value) {
                return;
            }
            rowEdit(index, item, key, value, changes, setChanges, jobTitle, setJobTitle);
        },
        [rowEdit, changes, setChanges, jobTitle, setJobTitle]
    );

    const handleCancelChanges = () => {
        setJobTitle(originalJobTitle);
        setChanges({});
        setDeletedItems([]);
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    };


    const validateInput = () => {
        for (const change of Object.values(changes)) {
            if (!change.jobtitle || change.jobtitle.trim() === "") {
                return [false, t("common:requiredJTC")];
            }
        }
        return [true, ""];
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);

            // Handle deletion first 
            if (deletedItems.length > 0) {
                const deleteResult = await deleteJobTitles(deletedItems, props.selectedCompany);
                if (!deleteResult) {
                    showMessage({
                        message: t("common:failedDelete"),
                        type: "danger",
                        duration: 3000,
                    });
                    setIsLoading(false);
                    return; // Stop further processing if delete failed
                } else {
                    showMessage({
                        message: t("common:successDelete"),
                        type: "success",
                        duration: 3000,
                    });
                    removeFromChangesAfterSave();
                }
            }

            // Handle save next with immediate message
            if (Object.entries(changes).length > 0) {
                // Validate jobtitle presence before saving
                const [isValid, validationMessage] = validateInput();
                if (!isValid) {
                    showMessage({
                        message: validationMessage,
                        type: "danger",
                        duration: 3000,
                    });
                    setIsLoading(false);
                    return;
                }

                const changedJobTitles = Object.values(changes).map(change => ({
                    jobtitle: change.jobtitle ?? "",
                    description: change.description ?? "",
                    abbreviation: change.abbreviation ?? "",
                    JobtitleDME: change.jobtitleDME ?? "",
                    Rowguid: change.rowguid && change.rowguid !== "" ? change.rowguid : uuidv4(),
                    d365Code: d365 ? (change.d365Code ?? "") : "",
                    alternativeDescription: d365 ? (change.alternativeDescription ?? "") : "",
                    alternativeAbbreviation: d365 ? (change.alternativeAbbreviation ?? "") : "",
                    UserID: userId,
                }));

                const saveResult = await saveJobTitles(changedJobTitles, props.selectedCompany);
                if (!saveResult) {
                    showMessage({
                        message: t("common:failedSave"),
                        type: "danger",
                        duration: 3000,
                    });
                    setIsLoading(false);
                    return;
                } else {
                    showMessage({
                        message: t("common:successSave"),
                        type: "success",
                        duration: 3000,
                    });
                    removeFromChangesAfterSave();
                }
            }

            // If no changes or deletions were made, notify user
            if (deletedItems.length === 0 && Object.entries(changes).length === 0) {
                showMessage({
                    message: t("common:noChanges"),
                    type: "info",
                    duration: 3000,
                });
            }

            // Reload data after save/delete
            await loadJobTitles();

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error in save/delete operations:", error);
            showMessage({
                message: t("common:errorSave"),
                type: "danger",
                duration: 3000,
            });
        }
    };

    const sortJobTitles = (list, order, column = "jobtitle") => {
        if (!order) return list;
        return [...list].sort((a, b) => {
            const valA = (a[column] || "").toString().toLowerCase();
            const valB = (b[column] || "").toString().toLowerCase();
            if (valA < valB) return order === "asc" ? -1 : 1;
            if (valA > valB) return order === "asc" ? 1 : -1;
            return 0;
        });
    };

    const handleDelete = (item, index,) => {
        setDeletedItems(prev => [...(prev || []), item]);
        rowDelete(item, index, "rowGuid", changes, setChanges, jobTitle, setJobTitle, setDeletedItems, ref)
    };

    const renderData = useCallback((item, index) => {
        return (
            <Card style={[common.cardstyle]} >
                <Card.Content>
                    <JobTitlesListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        styles={styles}
                        duplicates={duplicates}
                        t={t}
                        handleDelete={handleDelete}
                        d365={d365}
                    />
                </Card.Content>
            </Card>
        )
    }, [jobTitle, duplicates]);

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={styles.container}>
                <View style={styles.buttonsRow}>

                    {/* Left side: Sort button */}
                    <View style={styles.leftButtons}>
                        <TouchableOpacity
                            style={styles.sortButtonContainer}
                            onPress={() => {
                                const newOrder = sortOrder === "asc" ? "desc" : "asc";
                                setSortOrder(newOrder);
                                const sortedList = sortJobTitles(jobTitle, newOrder);
                                setJobTitle(sortedList);
                            }}
                        >
                            {/*FOR FUTURE- FITLER BY COLUMN*/
                            /* <Ionicons name="funnel-outline" size={30} style={styles.sortIcon} /> */}
                            <Text style={styles.sortText}>
                                {sortOrder === "asc" ? t("common:sortDesc") : t("common:sortAsc")}
                            </Text>
                            <Ionicons
                                name={sortOrder === "asc" ? "arrow-down-outline" : "arrow-up-outline"}
                                size={30}
                                style={styles.sortArrow}
                            />
                        </TouchableOpacity>
                    </View>

                    {/* Right side: New, Save, Cancel buttons */}
                    <View style={styles.rightButtons}>
                        <CustomButton
                            text={t("common:new")}
                            onPress={() => {
                                rowAdd(
                                    jobTitle,
                                    setJobTitle,
                                    ["rowGuid", "employeeId"],
                                    [],
                                    ref,
                                    t,
                                    ["employeeId"],
                                    ["type"],
                                    changes,
                                    setChanges,
                                    undefined,
                                    undefined,
                                    "rowGuid",
                                    false,
                                    ["date"],
                                    () => uuidv4()
                                );
                            }}
                            type="BUTTON_ADD"
                            textTop="0"
                            sz={15}
                        />
                        {(Object.keys(changes).length > 0 ||
                            changes.length > 0 ||
                            deletedItems.length > 0) && (
                                <CustomButton
                                    text={t("common:saveFunction")}
                                    onPress={handleSave}
                                    type="BUTTON_SAVE"
                                    isGradient={true}
                                    textTop="0"
                                    sz={15}
                                />
                            )}
                        <CustomButton
                            text={t("common:cancel")}
                            onPress={() => {
                                handleCancelChanges(originalJobTitle, setChanges, setDeletedItems, setDuplicates);
                            }}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                        />
                    </View>
                </View>

                <FlatList
                    data={jobTitle}
                    keyExtractor={(item) => String(item.rowguid ?? item.rowGuid)}
                    renderItem={({ item, index }) =>
                        renderData(item, index)}
                    refreshing={isLoading}
                    ref={ref}
                    ListHeaderComponent={
                        <SafeAreaView style={common.listTitle}>
                            <SafeAreaView style={styles.row}>
                                <CustomLabel value={t("common:jobTitleCode")} type="SUBTITLE" />
                                <CustomLabel value={t("common:description")} type="SUBTITLE" />
                                <CustomLabel value={t("common:abbreviation")} type="SUBTITLE" />
                                <CustomLabel value={t("common:regionalCode")} type="SUBTITLE" />
                            </SafeAreaView>
                            <SafeAreaView style={common.divider} />
                        </SafeAreaView>
                    }
                    ListEmptyComponent={
                        isLoading ? (
                            <ActivityIndicator animating={true} color={colors.blue} />
                        ) : (
                            <CustomLabel
                                value={t("common:noReviewDataPreview")}
                                type="NO_FUNCTIONS"
                            />
                        )
                    }
                    initialNumToRender={4}
                />
            </View>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) => StyleSheet.create({
    container: {
        flex: 1,
    },
    mainView: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
    },
    item: {
        flex: 1,
        justifyContent: "center",
        marginRight: 6,
    },
    sortButtonContainer: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 10,
        paddingVertical: 5,
    },
    sortIcon: {
        marginRight: 8,
    },
    sortText: {
        fontSize: 16,
        fontWeight: "600",
        marginRight: 8,
    },
    buttonsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginHorizontal: 15,
        marginVertical: 10,
    },
    leftButtons: {
        flexDirection: "row",
        alignItems: "center",
    },

    rightButtons: {
        flexDirection: "row",
        alignItems: "center",
    },
    loader: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "4%",
    },
    icons: {
        fontSize: 24,
        marginHorizontal: 10,
    },
});

export default HRAdminJobTitleScreen;